import React from 'react';
import logo from 'assets/images/logo-2-color.svg';
import { useTranslation } from 'react-i18next';
import LanguageSelector from 'components/LanguageSelector/LanguageSelector';
import dayjs from 'dayjs';
import { numbersDeclension } from 'utils/formatters';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ReactComponent as Telegram } from 'assets/icons/telegram.svg';
import { ReactComponent as Phone } from 'assets/icons/phone.svg';
import { useAppSelector } from 'hooks';
import duration from 'dayjs/plugin/duration';
import styles from './Maintenance.module.scss';

dayjs.extend(duration);

const MaintenancePage = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);
  const endTime = dayjs(user.maintenanceEndDate);
  const timeLeft = endTime.diff(dayjs());
  const initRemaining = dayjs.duration(timeLeft);
  const [hour, setHour] = React.useState(initRemaining.get('h'));
  const [minute, setMinute] = React.useState(initRemaining.get('m'));
  const [second, setSecond] = React.useState(initRemaining.get('s'));

  const timer = React.useRef<number | null>(null);

  if (timeLeft <= 1000) {
    return <Navigate to="/" replace />;
  }

  React.useEffect(() => {
    if (user.maintenanceEndDate === null) {
      navigate('/', { replace: true });
    }
    timer.current = window.setInterval(() => {
      const timeLeftNow = endTime.diff(dayjs());
      if (timeLeftNow <= 1000) {
        navigate('/', {
          replace: true,
        });
      }

      const remaining = dayjs.duration(timeLeftNow);
      setHour(remaining.get('h'));
      setMinute(remaining.get('m'));
      setSecond(remaining.get('s'));
    }, 1000);

    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, []);

  return (
    <main className={styles.maintenancePage}>
      <LanguageSelector className={styles.lang} />
      <Link to="/" className={styles.logo} qa-element="to-main">
        <img src={logo} width={200} height={80} alt="pro-data" />
      </Link>
      <h1 className="font-heading mb-4 text-uppercase">{t('maintenance.header')}</h1>
      <p className="font-body-m mb-5">{t('maintenance.description_1')}</p>
      <p className="mb-3">
        {t('maintenance.open_after')}
      </p>
      <div className={styles.timer}>
        <div className={styles.frame}>
          <div className={styles.number}>{(`0${hour}`).slice(-2)}</div>
          <div className={styles.label}>
            {numbersDeclension(hour, [
              t('common.hour_number.1'),
              t('common.hour_number.3'),
              t('common.hour_number.5'),
            ])}
          </div>
        </div>
        <div className={styles.divider}>:</div>
        <div className={styles.frame}>
          <div className={styles.number}>{(`0${minute}`).slice(-2)}</div>
          <div className={styles.label}>
            {numbersDeclension(minute, [
              t('common.minute_number.1'),
              t('common.minute_number.3'),
              t('common.minute_number.5'),
            ])}
          </div>
        </div>
        <div className={styles.divider}>:</div>
        <div className={styles.frame}>
          {' '}
          <div className={styles.number}>{(`0${second}`).slice(-2)}</div>
          <div className={styles.label}>
            {numbersDeclension(second, [
              t('common.second_number.1'),
              t('common.second_number.3'),
              t('common.second_number.5'),
            ])}
          </div>
        </div>
      </div>
      <div className="mt-auto align-self-start">
        <div className="mt-auto mb-2">{t('maintenance.contact_us')}</div>
        <a className="text-primary text-decoration-none me-3" target="_blank" href="https://t.me/PRO_DATA_Support_Bot" rel="noreferrer">
          <Telegram className="me-1" />
          <span>@PRO_DATA_Support_Bot</span>
        </a>
        <a className="text-primary text-decoration-none" href="tel:+998712032300">
          <Phone className="me-1" />
          <span>+998 71 203 23 00</span>
        </a>
      </div>
    </main>
  );
};

export default MaintenancePage;
